import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { DownloadOutlined } from '@ant-design/icons';
import { Button, Card, Spin, Row, Col, Select } from 'antd';

import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import { saveAs } from 'file-saver';
import { formatDate } from 'helpers/date';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { tap } from 'rxjs/operators';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ValueCard from 'v2/components/Shared/ValueCard';
import type { ICardProps } from 'v2/components/Shared/ValueCard';
import { SuspendChartTypes } from 'v2/interfaces/reports/suspend';
import { suspendService } from 'v2/services/reports/suspend';

import { selectTypeChart } from './style';

interface ICustomLabel {
  x: number;
  y: number;
  width: number;
  height: number;
  value: number;
}

interface IChartData {
  name: string;
  value: number;
}

export function ChartTab() {
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [typeChart, setTypeChart] = useState<string>(SuspendChartTypes.PERCENTAGE);
  const [chartData] = useObservable(() => suspendService.getChart(), []);
  const [isLoading] = useObservable(() => suspendService.isLoading(), []);
  const [handleExport] = useCallbackObservable(() => {
    setLoadingExport(true);
    return suspendService.export().pipe(
      tap(
        data => {
          try {
            const currentDate = formatDate(new Date(), 'dd-MM-yyyy');
            saveAs(data, `Contratos_MotivosSuspensao-${currentDate}.xlsx`);
          } catch (e) {
            console.log(e);
          } finally {
            setLoadingExport(false);
          }
        },
        err => {
          setLoadingExport(false);
          toast.error('Algo de errado aconteceu, tente exportar novamente.');
          console.log(err);
        }
      )
    );
  }, []);

  const chart = chartData?.chart || [];
  const subscriptionsAmount = chartData?.subscriptions_amount || 0;
  const subscriptionsTotal = chartData?.subscriptions_total || 0;

  const incomeCards: ICardProps[] = useMemo(
    () => [
      {
        title: 'Total de suspensões',
        total: !!isLoading ? '-' : subscriptionsTotal,
        helpText: 'Quantia total de contratos suspensos'
      },
      {
        title: 'Valor total',
        total: !!isLoading ? '-' : <ColoredFormattedNumber value={subscriptionsAmount} format='BRL' />,
        helpText: 'Valor total de contratos suspensos'
      }
    ],
    [isLoading, subscriptionsAmount, subscriptionsTotal]
  );

  const handleChangeChart = (value: string) => {
    return setTypeChart(value);
  };

  const data: Array<IChartData> = chart.map(item => ({
    name: item.reason,
    value: typeChart === SuspendChartTypes.PERCENTAGE ? item.percentage : item.amount
  }));

  const maxDataValue = Math.max(...data.map(i => i.value));
  const domainMax = Math.round(maxDataValue + maxDataValue * 0.5);

  const CustomizedLabel = ({ x, y, width, height, value }: ICustomLabel) => {
    const formatValue = (valueToFormat: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valueToFormat);
    const formattedValue = typeChart === SuspendChartTypes.PERCENTAGE ? `${value}%` : formatValue(value);
    return (
      <text x={x + width + 10} y={y + height / 2 + 10} fill='#666' textAnchor='right' dy={-6}>
        {formattedValue}
      </text>
    );
  };

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: '10px' }}>
        {incomeCards.map(card => (
          <Col xs={24} sm={12} md={6} key={card.title}>
            <ValueCard {...card} />
          </Col>
        ))}
      </Row>

      <Card variant='borderless'>
        <Row>
          {data.length === 0 ? (
            <Col span={24}>
              <p className='text-center text-base'>Nenhum registro encontrado.</p>
            </Col>
          ) : (
            <>
              <Col xs={24} sm={12} md={6} style={selectTypeChart}>
                <Select
                  className='w-full'
                  value={typeChart}
                  onChange={handleChangeChart}
                  options={[
                    { value: SuspendChartTypes.PERCENTAGE, label: 'Motivos por percentual (%)' },
                    { value: SuspendChartTypes.CURRENCY, label: 'Motivos por valor' }
                  ]}
                />
              </Col>

              <PermissionHide action={'myeduzz:reports:exportar'}>
                <Col xs={24} sm={12} md={18} style={{ textAlign: 'right', color: '#192542' }}>
                  <Button
                    disabled={!!isLoading || !!loadingExport}
                    onClick={handleExport}
                    type='text'
                    icon={
                      isLoading || loadingExport ? <Spin size='small' /> : <DownloadOutlined style={{ fontSize: 24 }} />
                    }
                  >
                    Exportar
                  </Button>
                </Col>
              </PermissionHide>

              <Col span={24} style={{ height: 400, overflowX: 'auto', overflowY: 'hidden' }}>
                <ResponsiveContainer width='100%' height='100%' minWidth={800}>
                  <BarChart layout='vertical' data={data} barCategoryGap='25%' margin={{ top: 30 }}>
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                    <XAxis type='number' axisLine={false} domain={[0, domainMax]} hide />
                    <YAxis type='category' dataKey='name' width={450} />
                    <Bar dataKey='value' fill='#8884d8' isAnimationActive={false} label={CustomizedLabel} />
                  </BarChart>
                </ResponsiveContainer>
              </Col>
            </>
          )}
        </Row>
      </Card>
    </>
  );
}
